import { ReceiptText, RotateCcw, Search } from 'lucide-react'
import { toast } from 'sonner'

import { useSearchParams } from 'react-router-dom'

import { useState } from 'react'

import { Badge, Dialog, DialogContent, DialogTitle, EmptyState, Input, PosButton, Segmented } from '@/components/ui'
import { CancelDialog } from '@/components/pos/orders/CancelDialog'
import { OrderHistory } from '@/components/pos/orders/OrderHistory'
import { PageHeader } from '@/components/pos/PageHeader'
import { Receipt } from '@/components/pos/checkout/Receipt'
import { PAYMENT_METHODS } from '@/constants/payment'
import { ReturnDialog } from '@/components/pos/orders/ReturnDialog'
import { useRegisterContext } from '@/contexts/RegisterContext'
import { useSession } from '@/contexts/SessionContext'
import { useOrders, useRefundOrder } from '@/hooks/features/orders'
import { useDebounce } from '@/hooks/ui/useDebounce'
import { cn } from '@/lib/utils'
import { PosModel } from '@/types'
import { usePrinter } from '@/services/printer'
import { formatMoney, formatTime } from '@/utils'

const OrderStatus = ({ order: o }: { order: PosModel.Order }) => (
  <span className="flex shrink-0 items-center gap-1.5">
    <Badge tone={o.status === 'completed' ? 'success' : o.status === 'cancelled' ? 'error' : 'warning'} className="capitalize">
      {o.status.replace('_', ' ')}
    </Badge>
    {/* Whether the server has it yet (§15.8 U5); refused ones are in the sync panel (U6). */}
    {o.sync_state === 'pending' && <span className="text-xs text-muted-foreground">waiting to sync</span>}
    {o.sync_state === 'rejected' && <span className="text-xs text-destructive">needs attention</span>}
  </span>
)

/** Today's sales on this register: an admin index table on the left, the selected receipt and its actions on the right. */
/** `shiftOnly`: the register's Orders tab, this terminal's sales in the open shift; the rail's Orders page shows all. */
export const OrdersPage = ({ shiftOnly = false }: { shiftOnly?: boolean }) => {
  const register = useRegisterContext()
  // A scanned receipt on the sale screen opens here with its ref (POS.md §18.8).
  const [params] = useSearchParams()
  const [search, setSearch] = useState(() => params.get('search') ?? '')
  const [selectedId, setSelectedId] = useState<string>()
  const debounced = useDebounce(search)
  const { data, isLoading } = useOrders(debounced, shiftOnly)
  const refund = useRefundOrder()
  const { stage, can } = useSession()
  const { printReceipt } = usePrinter()
  // Device orders return item by item; the demo API refunds whole orders.
  const [returning, setReturning] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  // `device`: the sales this till holds (return, cancel, reprint); `history`: the branch's sales on the server (B15).
  const [tab, setTab] = useState<'device' | 'history'>('device')
  const orders = data?.items ?? []
  const selected = orders.find((o) => o._id === selectedId) ?? orders[0]

  const openOrder = (id: string) => {
    setSelectedId(id)
    // Below lg there is no side panel; the receipt and its actions open over the list.
    if (!window.matchMedia('(min-width: 1024px)').matches) setDetailOpen(true)
  }

  const revenue = orders.filter((o) => o.status === 'completed').reduce((s, o) => s + o.totals.total, 0)

  const detail = selected && (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="shadow-z8">
          <Receipt order={selected} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 border-t border-border bg-card p-4">
        <PosButton variant="outline" size="touch" onClick={() => printReceipt(selected)}>
          Reprint
        </PosButton>
        {can('pos:return_order') && (
        <PosButton
          variant="destructive"
          size="touch"
          disabled={selected.status === 'refunded' || selected.status === 'cancelled' || selected.return_open === false || refund.isPending}
          title={selected.return_open === false ? 'The return window for this sale has closed' : undefined}
          onClick={() =>
            stage !== 'demo'
              ? setReturning(true)
              : refund.mutate(selected._id, {
                  onSuccess: (o) => toast.success(`${o.number} refunded`, { description: 'Items returned to stock.' }),
                  onError: (e) => toast.error(e.message),
                })
          }
        >
          <RotateCcw /> {stage !== 'demo' ? 'Return' : 'Refund'}
        </PosButton>
        )}
        {stage !== 'demo' && can('pos:cancel_order') && (
          <PosButton
            variant="outline"
            size="touch"
            className="col-span-2"
            disabled={!selected.cancellable}
            title={selected.cancellable ? undefined : 'Cancelled already, or items were returned'}
            onClick={() => setCancelling(true)}
          >
            Cancel sale
          </PosButton>
        )}
      </div>
    </>
  )

  if (tab === 'history') {
    return (
      <div className="flex h-full flex-col">
        <PageHeader title="Orders" subtitle="All sales of this branch, from the server.">
          <Segmented<'device' | 'history'> value={tab} onChange={setTab} options={[{ value: 'device', label: 'On this device' }, { value: 'history', label: 'History' }]} />
        </PageHeader>
        <OrderHistory />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={shiftOnly ? 'This shift' : 'Orders'}
        subtitle={
          shiftOnly
            ? register.opened_at
              ? `${orders.length} sale${orders.length === 1 ? '' : 's'} · ${formatMoney(revenue)} taken since ${formatTime(register.opened_at)}`
              : 'No shift is open on this terminal.'
            : `${orders.length} recent sale${orders.length === 1 ? '' : 's'} · ${formatMoney(revenue)} taken`
        }
      >
        {stage !== 'demo' && !shiftOnly && (
          <Segmented<'device' | 'history'> value={tab} onChange={setTab} options={[{ value: 'device', label: 'On this device' }, { value: 'history', label: 'History' }]} />
        )}
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Order number or customer" onCameraScan={setSearch} className="pl-9" />
        </div>
      </PageHeader>

      {!isLoading && orders.length === 0 ? (
        <EmptyState icon={ReceiptText} title={debounced ? 'No matching orders' : shiftOnly ? 'No sales yet this shift' : 'No sales yet'} className="flex-1">
          {debounced
            ? 'Try another order number or name.'
            : shiftOnly
              ? 'Ring up a sale on the Sale tab; it shows here with its receipt and a Return button.'
              : 'Completed sales from this register appear here.'}
        </EmptyState>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-h-0 overflow-auto">
            <ul className="divide-y divide-border-subtle sm:hidden">
              {orders.map((o) => (
                <li key={o._id}>
                  <button type="button" onClick={() => openOrder(o._id)} className="w-full px-4 py-3 text-left text-sm active:bg-subdued">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium text-ink">{o.number}</span>
                      <span className="shrink-0 font-medium tabular-nums text-ink">{formatMoney(o.totals.total)}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-3 text-xs text-text-2">
                      <span className="truncate">
                        {formatTime(o.created_at)} · {o.customer?.name ?? 'Walk-in'} · {o.payments.map((p) => PAYMENT_METHODS.find((m) => m.id === p.method)?.label).join(' + ')}
                      </span>
                      <OrderStatus order={o} />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            <table className="hidden w-full text-sm sm:table">
              <thead className="sticky top-0 bg-subdued text-left text-xs text-muted-foreground">
                <tr className="[&>th]:h-9 [&>th]:border-b [&>th]:border-border [&>th]:px-4 [&>th]:font-medium">
                  <th>Order</th>
                  <th>Time</th>
                  <th className="hidden md:table-cell">Customer</th>
                  <th className="hidden md:table-cell">Payment</th>
                  <th className="text-right">Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr
                    key={o._id}
                    onClick={() => openOrder(o._id)}
                    className={cn(
                      'cursor-pointer border-b border-border-subtle hover:bg-subdued [&>td]:h-11 [&>td]:px-4',
                      selected?._id === o._id && 'bg-selected/40 hover:bg-selected/40'
                    )}
                  >
                    <td className="font-medium text-ink">{o.number}</td>
                    <td className="tabular-nums text-text-2">{formatTime(o.created_at)}</td>
                    <td className="hidden text-text-2 md:table-cell">{o.customer?.name ?? 'Walk-in'}</td>
                    <td className="hidden text-text-2 md:table-cell">
                      {o.payments.map((p) => PAYMENT_METHODS.find((m) => m.id === p.method)?.label).join(' + ')}
                    </td>
                    <td className="text-right font-medium tabular-nums text-ink">{formatMoney(o.totals.total)}</td>
                    <td>
                      <OrderStatus order={o} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selected && (
            <aside className="hidden min-h-0 flex-col border-l border-border bg-subdued lg:flex">
              {detail}
            </aside>
          )}
        </div>
      )}
      {selected && (
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="flex max-h-[90dvh] flex-col gap-0 overflow-hidden bg-subdued p-0 lg:hidden">
            <DialogTitle className="sr-only">{selected.number}</DialogTitle>
            {detail}
          </DialogContent>
        </Dialog>
      )}
      {selected && stage !== 'demo' && <ReturnDialog order={selected} open={returning} onOpenChange={setReturning} />}
      {selected && stage !== 'demo' && <CancelDialog order={selected} open={cancelling} onOpenChange={setCancelling} />}
    </div>
  )
}
